import { z } from 'zod';
import { ApiError } from './errors.js';
/**
 * **Tax and tips** — the two numbers on a settlement that are not the shop's
 * (`docs/money-second-pass-design.md` §2–§3).
 *
 * It lives in the domain for `reservations.ts`' reason: the rule has to be
 * stated once and consumed by the till, the shop, the reports and the importer
 * alike. No I/O, no SQL, no HTTP — and, deliberately, no import from
 * `tenant.ts`, because `TenantConfig` imports *this* (the `tax` block is a
 * config field) and a cycle between two files that both export constants is how
 * a `const` becomes `undefined` at module-init time.
 *
 * ## The reality check that shapes the tax half
 *
 * The **US Virgin Islands has no general sales tax.** What it has is a gross
 * receipts tax, paid by the business on its own revenue, not added to a guest's
 * bill at a counter. So for the tenant this was built for, and for every tenant
 * on this island, the honest number is zero and it stays zero: a tenant with no
 * `tax` block behaves exactly as every tenant did before this file existed.
 * What is here is the smallest correct thing for a tenant somewhere else, and
 * the list of what it deliberately does **not** model is as load-bearing as
 * what it does.
 */
/* ------------------------------------------------------------------ */
/* Tax                                                                 */
/* ------------------------------------------------------------------ */
/**
 * The kinds of sellable a rate may apply to — `items.kind` (spec §7.3.1).
 *
 * Applying tax by **kind** rather than by a per-item class is a decision, not a
 * shortcut. `items.tax_class` exists and is already populated *from* the kind
 * (`'standard'`, `'prepared'`, `'lodging'`), so it carries no fact `kind` does
 * not; a per-item rate table would be a second vocabulary to keep in step, for
 * a rate that is zero on every tenant the platform has.
 */
export const TAXABLE_KINDS = ['scheduled', 'stocked', 'prepared', 'stay', 'departure'];
export const TaxableKind = z.enum(TAXABLE_KINDS);
/**
 * **A tenant's tax, in four fields** — `tenant_config.config.tax`, absent by
 * default and absent on every tenant today.
 *
 * `rate_bps` is basis points because a percentage with a decimal point is a
 * float, and this repository does not hold a money-adjacent number as a float
 * anywhere (dev.md §2). 725 is 7.25 %.
 *
 * `inclusive` is **accepted here and refused by `computeTax`**, on purpose. Tax
 * that is already inside the price would put tax inside `subtotal`, which
 * contradicts the one equation three separate reconcilers read structurally —
 * `subtotal − discount + tax = total` — and would need a fourth column plus a
 * rewrite of all three. Naming it and refusing it is honest; parsing it and
 * quietly computing the exclusive answer would not be.
 *
 * **Not modelled, and deliberately**: more than one rate, more than one
 * jurisdiction, tax-exempt customers, and tax on tips (there is none, anywhere).
 */
export const TaxConfig = z.object({
    /** Basis points. `0` is a real answer and means "a rate of zero", not "none". */
    rate_bps: z.number().int().min(0).max(10_000),
    /** Which item kinds the rate touches. Empty means nothing is taxable. */
    applies_to: z.array(TaxableKind).max(TAXABLE_KINDS.length),
    /** What the line is called on a till and a receipt. Display text, never a key. */
    label: z.string().min(1).max(40).optional(),
    /** Prices already include the tax. Refused by `computeTax` — see above. */
    inclusive: z.boolean().optional(),
});
/**
 * The tax on a basket, computed **once on the taxable total**.
 *
 * Rounding per line and summing would make the same basket cost different
 * amounts depending on how the cashier grouped it — two jars rung as one line of
 * two or as two lines of one — which is the kind of difference somebody notices
 * at a counter and nobody can explain. One half-up rounding on the taxable total
 * is deterministic in the basket rather than in its data entry.
 *
 * Returns `0` for an absent config, an empty `applies_to`, or a basket with
 * nothing taxable in it. Those three are genuinely the same answer.
 */
export function computeTax(config, lines) {
    if (config === null || config === undefined)
        return 0;
    if (config.inclusive === true) {
        throw ApiError.unsupported('tax-inclusive pricing — the ledger records tax beside the sale, never inside it');
    }
    if (config.rate_bps === 0 || config.applies_to.length === 0)
        return 0;
    const applies = new Set(config.applies_to);
    let taxable = 0;
    for (const line of lines) {
        if (applies.has(line.kind))
            taxable += line.chargedCents;
    }
    if (taxable <= 0)
        return 0;
    // Half-up on a non-negative number, in integer arithmetic. `Math.round` on
    // `taxable * bps / 10000` would put a cent through a float, which is the one
    // thing dev.md §2 says never happens anywhere.
    return Math.floor((taxable * config.rate_bps + 5_000) / 10_000);
}
/* ------------------------------------------------------------------ */
/* Tips                                                                */
/* ------------------------------------------------------------------ */
/**
 * The percentages the till offers as chips, of the **pre-tax subtotal**.
 *
 * Pre-tax, because tipping on tax is tipping the government, and because the
 * number a guest recognizes is a percentage of what the service cost.
 */
export const TIP_PERCENT_CHIPS = [15, 18, 20];
/**
 * A chip's amount. Half-up, the same integer arithmetic as `computeTax`.
 *
 * A tip is money collected **for a person**: never revenue, never a discount,
 * and never something an internal instrument can pay — see the waterfall, where
 * it is added after every instrument has drawn against the sale.
 */
export function tipFromPercent(subtotalBeforeTaxCents, percent) {
    if (subtotalBeforeTaxCents <= 0)
        return 0;
    return Math.floor((subtotalBeforeTaxCents * percent + 50) / 100);
}
//# sourceMappingURL=money.js.map